"use client"

import { Col, Empty, Row, Tabs } from "antd"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import SearchingHotelsItem from "./SearchingHotelsItem"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { FC, useMemo } from "react"
import { hotelActions } from "@/lib/redux/features/hotel/reduce"
import { useTranslation } from "react-i18next"
import Text from "@/components/common/Typography/Text"
import { groupDestinationsByCity, GroupedDestination } from "@/utils/hotel/groupDestinationsByCity"
import { useLocationType } from "@/hooks/hotel/useLocationType"
import { HotelLocation } from "@/lib/redux/features/hotel/type"

type HotelSearchDestinationResultProps = {
  onChange?: (hotel?: HotelLocation) => void
}

type DestinationGroupsProps = {
  groups: GroupedDestination[]
  onChange?: (hotel?: HotelLocation) => void
  labels: Record<string, string>
  icons: Record<string, IconDefinition>
  hotelLabel: string
  groupHeaderIcon: IconDefinition
}

const DestinationGroups: FC<DestinationGroupsProps> = ({
  groups,
  onChange,
  labels,
  icons,
  hotelLabel,
  groupHeaderIcon,
}) => {
  if (groups.length <= 0) {
    return (
      <div className="home__searching--destination-empty">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    )
  }

  return (
    <div className="home__searching--destination-list" role="list">
      {groups.map((group) => (
        <section key={group.key} className="home__searching--destination-group" role="listitem">
          {group.selectableLocation ? (
            <button
              type="button"
              className="home__searching--destination-group-header home__searching--destination-group-header--selectable"
              onClick={() => onChange?.(group.selectableLocation)}
            >
              <span className="home__searching--destination-group-icon-wrap" aria-hidden>
                <FontAwesomeIcon icon={groupHeaderIcon} />
              </span>
              <span className="home__searching--destination-group-header-text">
                <Text strong className="home__searching--destination-group-title">
                  {group.cityName}
                </Text>
                <Text type="secondary" className="home__searching--destination-group-meta">
                  {group.totalHotels.toLocaleString()} {hotelLabel}
                </Text>
              </span>
            </button>
          ) : (
            <header className="home__searching--destination-group-header">
              <span className="home__searching--destination-group-icon-wrap" aria-hidden>
                <FontAwesomeIcon icon={groupHeaderIcon} />
              </span>
              <span className="home__searching--destination-group-header-text">
                <Text strong className="home__searching--destination-group-title">
                  {group.cityName}
                </Text>
                <Text type="secondary" className="home__searching--destination-group-meta">
                  {group.totalHotels.toLocaleString()} {hotelLabel}
                </Text>
              </span>
            </header>
          )}
          <div className="home__searching--destination-group-items">
            {group.items.map((location) => (
              <SearchingHotelsItem
                key={location._id}
                location={location}
                onChange={onChange}
                labels={labels}
                icons={icons}
                hotelLabel={hotelLabel}
                nested
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

const HotelSearchDestinationResult: FC<HotelSearchDestinationResultProps> = ({ onChange }) => {
  const searchingDestination = useAppSelector((state) => state.hotel.searchingDestination)
  const hotelIds = useAppSelector((state) => state.hotel.hotels_locations_search.primaryKeys || [])
  const hotelsData = useAppSelector((state) => state.hotel.hotels_locations_search.data || {})
  const popularGroupsInternal = useAppSelector((state) => state.hotel.popularGroupsInternal)
  const popularGroupsExternal = useAppSelector((state) => state.hotel.popularGroupsExternal)
  const type = useAppSelector((state) => state.hotel.typeSearch)
  const loading = useAppSelector((state) => state.hotel.hotels_locations_search.loading)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { labels, icons, groupIcons } = useLocationType()
  const hotelLabel = t("common.hotel")

  const searchGroups = useMemo(
    () => groupDestinationsByCity(hotelIds, hotelsData),
    [hotelIds, hotelsData],
  )

  const sharedListProps = {
    onChange,
    labels,
    icons,
    hotelLabel,
    groupHeaderIcon: groupIcons.INTERNAL,
  }

  if (searchingDestination) {
    return (
      <div className="home__searching--destination-panel">
        {loading ? (
          <div className="home__searching--destination-loader">
            <div className="loader" />
          </div>
        ) : (
          <div className="home__searching--destination-scroll">
            <DestinationGroups groups={searchGroups} {...sharedListProps} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="home__searching--destination-panel">
      <Tabs
        className="home__searching--destination-tabs"
        destroyOnHidden
        onChange={(activeHotel: string) => {
          dispatch(hotelActions.setSearchType(activeHotel))
          dispatch(
            hotelActions.setPopularDestinations({
              show: activeHotel,
            }),
          )
        }}
        activeKey={type}
        items={[
          {
            label: t("hotel.popular_in_vietnam"),
            children: (
              <DestinationGroups
                groups={popularGroupsInternal}
                {...sharedListProps}
                groupHeaderIcon={groupIcons.INTERNAL}
              />
            ),
            key: "INTERNAL",
          },
          {
            label: t("hotel.internationally_popular"),
            children: (
              <DestinationGroups
                groups={popularGroupsExternal}
                {...sharedListProps}
                groupHeaderIcon={groupIcons.EXTERNAL}
              />
            ),
            key: "EXTERNAL",
          },
        ]}
      />
    </div>
  )
}

export default HotelSearchDestinationResult
